import { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { api, tokenStore } from '../api';
import { useAuth } from '../AuthContext';
import toast from 'react-hot-toast';
import NavIcon from '../components/NavIcon';

// ─── Visual helpers ───────────────────────────────────────────────────────────

const AVATAR_COLOURS = [
  '#CC7A3D','#B8562F','#8FA85E','#D4A24C',
  '#8B5FA8','#4E8B7C','#B8672E','#A8452F',
];
const avatarColour  = (id)   => AVATAR_COLOURS[(id ?? 0) % AVATAR_COLOURS.length];
const getInitials   = (name) => (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

function Avatar({ user, size = 34 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: avatarColour(user?.id),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: Math.round(size * 0.36), color: '#fff',
      userSelect: 'none', letterSpacing: '-0.5px',
    }}>
      {getInitials(user?.displayName || user?.username)}
    </div>
  );
}

function formatTime(dateStr) {
  const d   = new Date(dateStr);
  const now = new Date();
  const prev = new Date(now); prev.setDate(now.getDate() - 1);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === now.toDateString())  return time;
  if (d.toDateString() === prev.toDateString()) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { day: '2-digit', month: 'short' })} ${time}`;
}

// Consecutive messages from the same sender within 3 min → one visual group
function buildGroups(messages) {
  const groups = [];
  let cur = null;
  for (const msg of messages) {
    const same  = cur && cur.senderId === msg.sender.id;
    const close = cur && (new Date(msg.sentAt) - new Date(cur.lastAt)) < 3 * 60 * 1000;
    if (same && close) { cur.messages.push(msg); cur.lastAt = msg.sentAt; }
    else {
      cur = { senderId: msg.sender.id, sender: msg.sender, messages: [msg], lastAt: msg.sentAt };
      groups.push(cur);
    }
  }
  return groups;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function DateSep({ date }) {
  const d   = new Date(date);
  const now = new Date();
  const prev = new Date(now); prev.setDate(now.getDate() - 1);
  const label = d.toDateString() === now.toDateString()  ? 'Today'
    : d.toDateString() === prev.toDateString() ? 'Yesterday'
    : d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
  return <div className="chat-date-sep"><span>{label}</span></div>;
}

function Bubble({ msg, isMine, onDelete, canDeleteAll }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`chat-bubble ${isMine ? 'mine' : 'theirs'}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="chat-bubble-text">{msg.text}</span>
      <span className="chat-bubble-time">{formatTime(msg.sentAt)}</span>
      {(isMine || canDeleteAll) && hovered && (
        <button className="chat-delete-btn" onClick={() => onDelete(msg.id)} title="Delete"><NavIcon name="trash" size={12} /></button>
      )}
    </div>
  );
}

function MsgGroup({ group, isMine, onDelete, canDeleteAll }) {
  return (
    <div className={`chat-group ${isMine ? 'mine' : 'theirs'}`}>
      {!isMine && <Avatar user={group.sender} size={32} />}
      <div className="chat-group-body">
        {!isMine && (
          <div className="chat-sender-name">
            {group.sender.displayName || group.sender.username}
          </div>
        )}
        {group.messages.map(msg => (
          <Bubble key={msg.id} msg={msg} isMine={isMine} onDelete={onDelete} canDeleteAll={canDeleteAll} />
        ))}
      </div>
    </div>
  );
}

// ─── Connection status indicator ──────────────────────────────────────────────

const STATUS_LABEL = {
  connecting:    'Connecting…',
  connected:     'Live',
  reconnecting:  'Reconnecting…',
  disconnected:  'Disconnected',
};
const STATUS_CLASS = {
  connecting:   'offline',
  connected:    'online',
  reconnecting: 'offline',
  disconnected: 'offline',
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Chat() {
  const { user }                  = useAuth();
  const [messages, setMessages]   = useState([]);
  const [text, setText]           = useState('');
  const [loading, setLoading]     = useState(true);
  const [sending, setSending]     = useState(false);
  const [connStatus, setConnStatus] = useState('connecting');

  const bottomRef   = useRef(null);
  const inputRef    = useRef(null);
  const hubRef      = useRef(null);
  const nearBottom  = useRef(true);

  const scrollToBottom = (smooth = false) =>
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });

  const handleScroll = (e) => {
    const el = e.currentTarget;
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
  };

  // ── Append + dedup helper ─────────────────────────────────────────────────
  const appendMessages = useCallback((incoming) => {
    if (!incoming.length) return;
    setMessages(prev => {
      const ids   = new Set(prev.map(m => m.id));
      const fresh = incoming.filter(m => !ids.has(m.id));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
    if (nearBottom.current) setTimeout(() => scrollToBottom(true), 30);
  }, []);

  // ── Load history ──────────────────────────────────────────────────────────
  useEffect(() => {
    api.getChatMessages(100)
      .then(msgs => {
        setMessages(msgs);
        setLoading(false);
        setTimeout(() => scrollToBottom(false), 50);
      })
      .catch(() => { toast.error('Failed to load messages'); setLoading(false); });
  }, []);

  // ── SignalR connection ────────────────────────────────────────────────────
  useEffect(() => {
    const token = tokenStore.get();
    if (!token) return;

    const hub = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/chat', {
        // The browser WebSocket API doesn't support custom headers, so
        // SignalR passes the token via query string: ?access_token=<jwt>
        // The server reads it in JwtBearerEvents.OnMessageReceived (Program.cs)
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // retry schedule in ms
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // ── Hub event handlers ────────────────────────────────────────────────
    hub.on('ReceiveMessage', (msg) => {
      appendMessages([msg]);
    });

    hub.on('MessageDeleted', (id) => {
      setMessages(prev => prev.filter(m => m.id !== id));
    });

    // ── Connection lifecycle ──────────────────────────────────────────────
    hub.onreconnecting(() => setConnStatus('reconnecting'));
    hub.onreconnected(() => setConnStatus('connected'));
    hub.onclose(() => setConnStatus('disconnected'));

    setConnStatus('connecting');
    hub.start()
      .then(() => setConnStatus('connected'))
      .catch((err) => {
        console.error('SignalR connection failed:', err);
        setConnStatus('disconnected');
        toast.error('Chat connection failed — refresh to retry');
      });

    hubRef.current = hub;

    return () => {
      hub.stop();
    };
  }, [appendMessages]);

  // ── Send ──────────────────────────────────────────────────────────────────
  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setText('');
    setSending(true);
    try {
      // POST to REST endpoint → server saves + broadcasts via hub → our
      // hub.on('ReceiveMessage') fires and adds it. No manual local append.
      await api.sendChatMessage(trimmed);
    } catch (err) {
      toast.error(err.message || 'Failed to send');
      setText(trimmed); // restore on failure
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await api.deleteChatMessage(id);
      // hub.on('MessageDeleted') will remove it from state for everyone
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  // ── Build render list: date separators → grouped bubbles ─────────────────
  const renderItems = [];
  let currentDate   = null;
  let pendingMsgs   = [];

  const flushPending = () => {
    if (!pendingMsgs.length) return;
    buildGroups(pendingMsgs).forEach(g =>
      renderItems.push({ type: 'group', group: g, key: `g-${g.messages[0].id}` })
    );
    pendingMsgs = [];
  };

  for (const msg of messages) {
    const dk = new Date(msg.sentAt).toDateString();
    if (dk !== currentDate) {
      flushPending();
      renderItems.push({ type: 'date', date: msg.sentAt, key: `d-${dk}` });
      currentDate = dk;
    }
    pendingMsgs.push(msg);
  }
  flushPending();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="chat-page">

      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="chat-header-icon"><NavIcon name="chat" size={18} /></div>
          <div>
            <div className="chat-header-title">Team Chat</div>
            <div className="chat-header-sub">Group · all staff</div>
          </div>
        </div>
        <div className={`chat-status ${STATUS_CLASS[connStatus]}`}>
          <span className="chat-status-dot" />
          {STATUS_LABEL[connStatus]}
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages" onScroll={handleScroll}>
        {loading ? (
          <div className="chat-empty"><div className="spinner" /></div>
        ) : messages.length === 0 ? (
          <div className="chat-empty">
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><NavIcon name="chat" size={40} /></div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>No messages yet</div>
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>Be the first to say something!</div>
          </div>
        ) : (
          <>
            {renderItems.map(item =>
              item.type === 'date'
                ? <DateSep key={item.key} date={item.date} />
                : <MsgGroup
                    key={item.key}
                    group={item.group}
                    isMine={item.group.senderId === user?.id}
                    onDelete={handleDelete}
                    canDeleteAll={user?.isAdmin}
                  />
            )}
            <div ref={bottomRef} style={{ height: 1 }} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="chat-input-bar">
        <div className="chat-input-wrap">
          <textarea
            ref={inputRef}
            className="chat-input"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKey}
            placeholder={
              connStatus === 'connected'
                ? 'Type a message… (Enter to send, Shift+Enter for new line)'
                : 'Connecting to chat…'
            }
            rows={1}
            maxLength={2000}
            disabled={sending || connStatus !== 'connected'}
          />
          <button
            className={`chat-send-btn ${text.trim() && connStatus === 'connected' ? 'active' : ''}`}
            onClick={send}
            disabled={!text.trim() || sending || connStatus !== 'connected'}
            title="Send (Enter)"
          >
            {sending
              ? <div className="spinner" style={{ width:16, height:16, borderWidth:2 }} />
              : <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
            }
          </button>
        </div>
        <div className="chat-input-hint">
          <kbd>Enter</kbd> send · <kbd>Shift+Enter</kbd> new line
        </div>
      </div>
    </div>
  );
}
